import { apiFetch } from "./api";
import type { Paginated } from "./platform-api";

export type AttendanceStatus =
  | "PRESENT"
  | "CHECKED_IN"
  | "CHECKED_OUT"
  | "PARTIAL"
  | "EXCEPTION"
  | "MISSED"
  | "OUTSIDE_GEOFENCE"
  | "PENDING_REVIEW";

export type GpsStatus = "VALID" | "INVALID" | "UNKNOWN";
export type GeofenceStatus = "INSIDE" | "OUTSIDE" | "NOT_APPLICABLE";
export type SessionCloseReason = "EMPLOYEE" | "SITE_SWITCH" | "ADMIN_CORRECTION";
/** Server-derived at check-in: at a site (WFO) or inside the home geofence (WFH). */
export type WorkMode = "WFO" | "WFH" | "WFA";
export type AttendanceSource = "MOBILE" | "WEB";

/** One check-in/check-out pair, as returned by `GET /attendance/:id`. */
export interface AttendanceSessionItem {
  id: string;
  attendanceRecordId: string;
  siteId: string | null;
  startAt: string;
  startLatitude: number | null;
  startLongitude: number | null;
  startGpsStatus: GpsStatus;
  startGeofenceStatus: GeofenceStatus;
  endAt: string | null;
  endLatitude: number | null;
  endLongitude: number | null;
  endGpsStatus: GpsStatus | null;
  endGeofenceStatus: GeofenceStatus | null;
  durationMinutes: number | null;
  closedBy: SessionCloseReason | null;
  notes: string | null;
  workMode: WorkMode;
  /**
   * Where the punch came from (FEAT-2026-013). `null` on sessions recorded
   * before provenance was tracked — not back-filled, since we never observed it.
   */
  startSource: AttendanceSource | null;
  endSource: AttendanceSource | null;
  /** The approved WFA request that permitted this punch, when workMode is WFA. */
  wfaRequestId: string | null;
  /** Accuracy radius in metres the client reported, when it reported one. */
  startAccuracyM: number | null;
  endAccuracyM: number | null;
}

export interface AttendanceItem {
  id: string;
  tenantId: string;
  employeeId: string;
  employeeName: string;
  employeeEmpCode: string;
  employeeDepartment: string | null;
  siteId: string | null;
  siteName: string | null;
  date: string;
  checkInAt: string | null;
  checkInLatitude: number | null;
  checkInLongitude: number | null;
  checkOutAt: string | null;
  checkOutLatitude: number | null;
  checkOutLongitude: number | null;
  gpsStatus: GpsStatus;
  geofenceStatus: GeofenceStatus;
  /** Mode of the most recent check-in this day (WFO for pre-feature rows). */
  workMode: WorkMode;
  status: AttendanceStatus;
  isException: boolean;
  durationMinutes: number | null;
  /**
   * Why a closed day is Partial (BR-155), kept at check-out: minutes below its
   * minimum hours, and minutes before its scheduled end. `null` = not
   * evaluated — never "met", which is `0`.
   */
  shortByMinutes: number | null;
  leftEarlyByMinutes: number | null;
  remarks: string | null;
  flaggedById: string | null;
  flaggedReason: string | null;
  approvedById: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Site location payload included in the admin detail response so the map
 * view can render without a follow-up `/sites/:id` fetch. Null when the
 * attendance record isn't tied to any site (unusual but legal).
 */
export interface AttendanceSiteDetail {
  id: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  geofenceRadiusM: number;
  geofenceMode: "GPS_AND_GEOFENCE" | "GPS_ONLY" | "MANUAL";
}

/**
 * Employee home geofence, shown on the detail map so HR can confirm a WFH
 * check-in sits inside the declared zone. Sensitive PII — the backend only
 * populates it for TENANT_OWNER / HR_MANAGER (BR-063); null otherwise.
 */
export interface AttendanceHomeDetail {
  latitude: number;
  longitude: number;
  radiusM: number;
}

/** Detail shape — same as list items, plus session timeline + site/home geo. */
export interface AttendanceDetail extends AttendanceItem {
  sessions: AttendanceSessionItem[];
  site: AttendanceSiteDetail | null;
  /** Present only for HR-tier viewers when the employee has set a home. */
  home: AttendanceHomeDetail | null;
}

export interface AttendanceSummary {
  date: string;
  totalRecords: number;
  checkedIn: number;
  checkedOut: number;
  present: number;
  partial: number;
  /** Records an admin has flagged — the set "Exceptions only" lists (BUG-2026-016). */
  exceptions: number;
  missed: number;
  outsideGeofence: number;
  pendingReview: number;
  /** WFO/WFH split across the records in scope. */
  /** Counted by exact mode; the three buckets reconcile to `totalRecords`. */
  workModes: { wfo: number; wfh: number; wfa: number };
}

export interface ListAttendanceQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  date?: string;
  dateFrom?: string;
  dateTo?: string;
  siteId?: string;
  /** Several sites (FEAT-2026-030). */
  siteIds?: string[];
  departmentId?: string;
  /** Several departments, each with its sub-units (FEAT-2026-030). */
  departmentIds?: string[];
  status?: AttendanceStatus;
  workMode?: WorkMode;
  employeeId?: string;
  exceptionsOnly?: boolean;
  assignedToMe?: boolean;
}

export interface SummaryAttendanceQuery {
  /** Single-day mode. If set, `dateFrom` / `dateTo` are ignored. */
  date?: string;
  /** Inclusive lower bound of a range. */
  dateFrom?: string;
  /** Inclusive upper bound of a range. */
  dateTo?: string;
  siteId?: string;
  /** Several sites (FEAT-2026-030). */
  siteIds?: string[];
  assignedToMe?: boolean;
}

export interface FlagAttendanceBody {
  reason?: string;
}

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    // Lists go as `a,b` (FEAT-2026-030); an empty one is no filter.
    if (Array.isArray(v) && v.length === 0) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const attendanceApi = {
  list: (q: ListAttendanceQuery = {}) =>
    apiFetch<Paginated<AttendanceItem>>(`/attendance${toQuery(q as Record<string, unknown>)}`),

  summary: (q: SummaryAttendanceQuery = {}) =>
    apiFetch<AttendanceSummary>(`/attendance/summary${toQuery(q as Record<string, unknown>)}`),

  get: (id: string) => apiFetch<AttendanceDetail>(`/attendance/${id}`),

  flag: (id: string, body: FlagAttendanceBody = {}) =>
    apiFetch<AttendanceItem>(`/attendance/${id}/flag`, { method: "PATCH", body }),
};
